
import { ResponsiveContainer, RadarChart, PolarGrid, PolarAngleAxis, Radar, Legend, Tooltip } from 'recharts';
import { Card } from "@/components/ui/card";
import { ChartBar } from "lucide-react";
import { Industry } from "@/types/assessment";

interface RadarAnalysisProps {
  dimensionScores: Record<string, number>;
  industry: Industry;
}

const getIndustryBenchmarks = (industry: string): Record<string, number> => {
  // These benchmarks are based on industry research and standards
  const benchmarks = {
    "Financial Services": {
      strategy: 4.2,
      talent: 3.8,
      data: 4.0,
      infrastructure: 3.9,
      operations: 3.7,
      innovation: 3.5
    },
    "Healthcare & Pharma": {
      strategy: 3.8,
      talent: 3.5,
      data: 3.9,
      infrastructure: 3.6,
      operations: 3.4,
      innovation: 3.3
    },
    "Manufacturing & Industry": {
      strategy: 3.5,
      talent: 3.2,
      data: 3.6,
      infrastructure: 3.8,
      operations: 3.9,
      innovation: 3.4
    },
    "Retail & E-commerce": {
      strategy: 3.6,
      talent: 3.4,
      data: 3.8,
      infrastructure: 3.5,
      operations: 3.6,
      innovation: 3.7
    },
    "Education & EdTech": {
      strategy: 3.2,
      talent: 3.1,
      data: 3.3,
      infrastructure: 3.2,
      operations: 3.0,
      innovation: 3.4
    },
    "Government & Public Sector": {
      strategy: 3.0,
      talent: 2.8,
      data: 3.1,
      infrastructure: 3.0,
      operations: 2.9,
      innovation: 2.7
    },
    "Real Estate": {
      strategy: 3.3,
      talent: 3.0,
      data: 3.4,
      infrastructure: 3.1,
      operations: 3.2,
      innovation: 3.0
    },
    "Other": {
      strategy: 3.4,
      talent: 3.3,
      data: 3.5,
      infrastructure: 3.4,
      operations: 3.3,
      innovation: 3.2
    }
  };

  return benchmarks[industry as keyof typeof benchmarks] || benchmarks["Other"];
};

const getRadarAnalysisText = (scores: Record<string, number>, benchmarks: Record<string, number>) => {
  const avgScore = Object.values(scores).reduce((a, b) => a + b, 0) / Object.values(scores).length;
  const avgBenchmark = Object.values(benchmarks).reduce((a, b) => a + b, 0) / Object.values(benchmarks).length;
  const strongestDimension = Object.entries(scores).reduce((a, b) => a[1] > b[1] ? a : b)[0];
  const weakestDimension = Object.entries(scores).reduce((a, b) => a[1] < b[1] ? a : b)[0];

  const compareToBenchmark = avgScore >= avgBenchmark ? "meets or exceeds" : "is below";

  return {
    overview: `Your organization's AI maturity profile ${compareToBenchmark} industry benchmarks, with an average score of ${avgScore.toFixed(1)} compared to the industry average of ${avgBenchmark.toFixed(1)}.`,
    strengths: `${strongestDimension.charAt(0).toUpperCase() + strongestDimension.slice(1)} emerges as your strongest dimension, indicating well-developed capabilities in this area.`,
    improvements: `${weakestDimension.charAt(0).toUpperCase() + weakestDimension.slice(1)} presents the greatest opportunity for improvement and should be a focus area for development.`,
    balance: `The radar visualization reveals the balance between different dimensions, highlighting areas where focused investment could enhance overall AI maturity.`
  };
};

export const RadarAnalysis = ({ dimensionScores, industry }: RadarAnalysisProps) => {
  const benchmarks = getIndustryBenchmarks(industry);
  
  const radarData = Object.entries(dimensionScores).map(([dimension, score]) => ({
    dimension: dimension.charAt(0).toUpperCase() + dimension.slice(1),
    score,
    benchmark: benchmarks[dimension.toLowerCase()]
  }));

  const analysis = getRadarAnalysisText(dimensionScores, benchmarks);

  return (
    <Card className="p-4">
      <div className="flex items-center gap-3 mb-6">
        <ChartBar className="w-6 h-6 text-primary" />
        <h3 className="text-xl font-semibold text-primary">Dimension Score Comparison</h3>
      </div>
      <div className="h-[400px] w-full">
        <ResponsiveContainer width="100%" height="100%">
          <RadarChart 
            cx="50%" 
            cy="50%" 
            data={radarData} 
            margin={{ top: 20, right: 30, bottom: 20, left: 30 }}
          >
            <PolarGrid stroke="#e5e7eb" />
            <PolarAngleAxis 
              dataKey="dimension" 
              stroke="#374151"
              tick={{ 
                fill: "#374151", 
                fontSize: 12,
                fontWeight: 500
              }}
            />
            <Radar
              name="Your Score"
              dataKey="score"
              stroke="#2563eb"
              fill="#2563eb"
              fillOpacity={0.5}
            />
            <Radar
              name="Industry Benchmark"
              dataKey="benchmark"
              stroke="#9333ea"
              fill="#9333ea"
              fillOpacity={0.3}
            />
            <Tooltip 
              contentStyle={{ 
                backgroundColor: 'white',
                border: '1px solid #e5e7eb',
                borderRadius: '6px',
                padding: '8px'
              }}
            />
            <Legend 
              verticalAlign="bottom" 
              align="center"
              wrapperStyle={{
                paddingTop: "20px"
              }}
            />
          </RadarChart>
        </ResponsiveContainer>
      </div>
      <div className="mt-6 space-y-4 p-4">
        <p className="text-gray-700 leading-relaxed">{analysis.overview}</p>
        <p className="text-gray-700 leading-relaxed">{analysis.strengths}</p>
        <p className="text-gray-700 leading-relaxed">{analysis.improvements}</p>
        <p className="text-gray-700 leading-relaxed">{analysis.balance}</p>
      </div>
    </Card>
  );
};
